import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import BackButton from "../components/BackButton";
import { LogOut, ChevronDown } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DynamicModuleService,
  DropdownService,
  RosterService,
  AuthService,
  UserPreferencesService,
} from "../services/APIs";
import OrganizationProfileDetailsService from "../services/APIs/OrganizationProfileDetails";
import CitycollabNavigationRoutes from "../utils/navigationRoutes";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import GoogleAddressInput from "../components/GoogleAddressInput";
import { Location } from "../utils/types/tenantUser";
import { genderOptions } from "../utils/formOptions";
import { notify } from "../components/common/Notification";

const Settings = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const basicInfoSchema = "MM Member";

  // Get memberId from URL params, API response, or localStorage
  const memberIdFromUrl = searchParams.get("memberId");
  const memberIdFromStorage = localStorage.getItem("memberId");

  const OrganizationId = localStorage.getItem("OrganizationId");
  const departmentId = localStorage.getItem("DepartmentId");
  const isDepartmentReadonly = true;
  const isPositionReadonly = true;

  // Dropdown state
  const [selectedPrefix, setSelectedPrefix] = useState<string>("");
  const [selectedSuffix, setSelectedSuffix] = useState<string>("");
  const [selectedGender, setSelectedGender] = useState<string>("");
  const [selectedDepartment, setSelectedDepartment] = useState<string>("");
  const [selectedPosition, setSelectedPosition] = useState<string>("");
  const [selectedSeniority, setSelectedSeniority] = useState<string>("");
  const [showPrefixOptions, setShowPrefixOptions] = useState(false);
  const [showSuffixOptions, setShowSuffixOptions] = useState(false);
  const [showGenderOptions, setShowGenderOptions] = useState(false);
  const [showDepartmentOptions, setShowDepartmentOptions] = useState(false);
  const [showPositionOptions, setShowPositionOptions] = useState(false);
  const [showSkillsOptions, setShowSkillsOptions] = useState(false);
  const [showSkillInterestsOptions, setShowSkillInterestsOptions] =
    useState(false);
  const [showSeniorityOptions, setShowSeniorityOptions] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState<string>("");
  const [selectedContract, setSelectedContract] = useState<string>("");
  const [selectedMarketplace, setSelectedMarketplace] = useState<string>("");
  const [selectedOpportunities, setSelectedOpportunities] =
    useState<string>("");
  const [showConsultationOptions, setShowConsultationOptions] = useState(false);
  const [showContractOptions, setShowContractOptions] = useState(false);
  const [showMarketplaceOptions, setShowMarketplaceOptions] = useState(false);
  const [showOpportunitiesOptions, setShowOpportunitiesOptions] =
    useState(false);
  const [addressDetails, setAddressDetails] = useState<Location | null>(null);
  const [cityAddressDetails, setCityAddressDetails] = useState<Location | null>(
    null
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // All form state fields
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [profileimg, setProfileimg] = useState<string | null>(null);
  const [certificate1, setCertificate1] = useState("");
  const [phonenumber, setPhonenumber] = useState("");
  const [dob, setDob] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [cityaddress, setCityaddress] = useState<string>("");
  const [description, setDescription] = useState("");
  const [workemail, setWorkemail] = useState("");
  const [skill, setSkill] = useState<string[]>([]);
  const [skillinterests, setSkillinterests] = useState<string[]>([]);
  const [supervisor, setSupervisor] = useState("");
  const [responsibility, setResponsibility] = useState("");
  const [emergencyroles, setEmergencyroles] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [twitter, setTwitter] = useState("");
  const [facebook, setFacebook] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [email, setEmail] = useState("");

  // Skill interests options
  const skillInterestsOptions = [
    { label: "Computer", value: "computer" },
    { label: "Math", value: "math" },
    { label: "Science", value: "science" },
    { label: "Arts", value: "arts" },
    { label: "Music", value: "music" },
    { label: "Sports", value: "sports" },
    { label: "Leadership", value: "leadership" },
    { label: "Commnication", value: "Commnication" },
  ];

  // Seniority options
  const seniorityOptions = [
    { label: "Director", value: "director" },
    { label: "Manager", value: "manager" },
    { label: "Supervisor", value: "supervisor" },
    { label: "Staff", value: "staff" },
    { label: "Intern", value: "intern" },
  ];

  // Fetch dropdown options
  const { data: prefixOptions } = useQuery({
    queryKey: ["prefix_child"],
    queryFn: () => DropdownService.getDropdownData("prefix_child", false),
  });

  const { data: suffixOptions } = useQuery({
    queryKey: ["suffix"],
    queryFn: () => DropdownService.getDropdownData("suffix", false),
  });

  const { data: departmentOptions } = useQuery({
    queryKey: ["department_list"],
    queryFn: async () => {
      // Calls: /formily/dynamic-model/Department/grid_data/?page=1&page_size=10
      const items = await DynamicModuleService.getGridColItems(
        "Department",
        1,
        10,
        false
      );
      return Array.isArray(items)
        ? items.filter((item: any) => item.is_active !== false)
        : [];
    },
  });

  const { data: positionOptions } = useQuery({
    queryKey: ["position_list"],
    queryFn: async () => {
      // Calls: /formily/dynamic-model/Position/grid_data/?page=1&page_size=10
      const items = await DynamicModuleService.getGridColItems(
        "Position",
        1,
        10,
        false
      );
      return Array.isArray(items)
        ? items.filter((item: any) => item.is_active !== false)
        : [];
    },
  });

  const { data: skillsOptions } = useQuery({
    queryKey: ["skill_interest"],
    queryFn: () => DropdownService.getDropdownData("skill_interest", false),
  });

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowPrefixOptions(false);
      setShowSuffixOptions(false);
      setShowGenderOptions(false);
      setShowSkillsOptions(false);
      setShowPositionOptions(false);
      setShowDepartmentOptions(false);
      setShowSkillInterestsOptions(false);
      setShowSeniorityOptions(false);
      setShowConsultationOptions(false);
      setShowContractOptions(false);
      setShowMarketplaceOptions(false);
      setShowOpportunitiesOptions(false);
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Pre-select department if departmentId exists
  useEffect(() => {
    if (departmentId && departmentOptions && !selectedDepartment) {
      const department = Array.isArray(departmentOptions)
        ? departmentOptions.find(
          (dept: any) =>
            dept.id === departmentId || dept.id === Number(departmentId)
        )
        : null;
      if (department) {
        setSelectedDepartment(department.id);
      }
    }
  }, [departmentId, departmentOptions, selectedDepartment]);

  // Fetch member profile using DynamicModuleService
  const { data: memberProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ["memberProfile", basicInfoSchema],
    queryFn: async () => {
      // First try to get memberId from URL, then localStorage
      const memberId = memberIdFromUrl ?? memberIdFromStorage;

      if (!memberId) {
        console.error("Member ID is missing");
        return null;
      }

      try {
        console.log("Fetching member profile with:", {
          schema: basicInfoSchema,
          memberId: memberId,
          url: `/formily/dynamic-model/${encodeURIComponent(basicInfoSchema)}/${memberId}/`,
        });
        const response = await DynamicModuleService.getItemById(
          basicInfoSchema,
          memberId,
          false
        );
        console.log("Fetched member profile response:", response);
        if (response) {
          return response;
        }

        // Fallback: search by email if response is null (likely 404 handled by service)
        console.log("Response was null, trying fallback search by email");
        const userInfo = localStorage.getItem("userInfo");
        if (userInfo) {
          const user = JSON.parse(userInfo);
          const email = user.email ?? user.user_email;
          if (email) {
            const checkMemberResponse =
              await RosterService.checkMemberByEmail(email);
            if (
              checkMemberResponse?.success === true &&
              checkMemberResponse?.data?.id
            ) {
              const newMemberId = checkMemberResponse.data.id;
              console.log("Found member via email fallback:", newMemberId);
              localStorage.setItem("memberId", newMemberId);
              // Return the data from the fallback check
              return checkMemberResponse.data;
            }
          }
        }
        return null;
      } catch (error: any) {
        console.error("Error fetching member profile:", error);

        // Fallback: search by email on error (like 404)
        const userInfo = localStorage.getItem("userInfo");
        if (userInfo) {
          try {
            const user = JSON.parse(userInfo);
            const email = user.email ?? user.user_email;
            if (email) {
              console.log(
                "Attempting fallback search by email on error for:",
                email
              );
              const checkMemberResponse =
                await RosterService.checkMemberByEmail(email);
              if (
                checkMemberResponse?.success === true &&
                checkMemberResponse?.data?.id
              ) {
                const newMemberId = checkMemberResponse.data.id;
                console.log(
                  "Found member via email fallback after error:",
                  newMemberId
                );
                localStorage.setItem("memberId", newMemberId);
                return checkMemberResponse.data;
              }
            }
          } catch (innerError) {
            console.error("Fallback search failed:", innerError);
          }
        }

        console.error("Error details:", {
          message: error?.message,
          status: error?.response?.status,
          statusText: error?.response?.statusText,
          data: error?.response?.data,
        });
        return null;
      }
    },
    enabled: !!(memberIdFromUrl ?? memberIdFromStorage) && !!basicInfoSchema,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    staleTime: 30000,
    retry: false,
  });

  // Get the final memberId from response or params/storage
  const memberId =
    memberProfile?.id ??
    memberProfile?.member_id ??
    memberIdFromUrl ??
    memberIdFromStorage;

  // Update member profile mutation using DynamicModuleService
  const updateProfileMutation = useMutation({
    mutationFn: async (payload: any) => {
      if (!memberId) {
        throw new Error("Member ID is required");
      }
      console.log("Updating with payload:", payload);
      console.log(
        "API endpoint:",
        `/formily/dynamic-model/${encodeURIComponent(basicInfoSchema)}/${memberId}/`
      );
      const response = await DynamicModuleService.updateItem(
        basicInfoSchema,
        memberId,
        payload,
        false
      );
      console.log("Update response:", response);
      return response;
    },
    onSuccess: () => {
      // Invalidate and refetch member profile data
      queryClient.invalidateQueries({
        queryKey: ["memberProfile", basicInfoSchema],
      });
      notify.success("Profile updated successfully");
      console.log("Profile updated successfully");
      // Navigate to profile page after successful update
      navigate(CitycollabNavigationRoutes.PROFILE);
    },
    onError: error => {
      console.error("Error updating profile:", error);
      const errorMessage =
        (error as any)?.response?.data?.message ??
        (error as any)?.message ??
        "An unexpected error occurred while saving.";
      notify.error(errorMessage);
    },
  });

  /** Google Places uses `place_id`; API may return a record `id` — both are valid structured payloads */
  const hasStructuredAddressPayload = (loc: Location | null): boolean => {
    if (!loc) return false;
    return !!(
      loc.place_id?.trim() ||
      (loc as { id?: string }).id ||
      (loc.formatted_address?.trim() &&
        (loc.latitude?.length || loc.longitude?.length))
    );
  };

  // Handle address changes
  const handleAddressChange = (details?: Location) => {
    if (details) {
      setAddress(details.formatted_address);
      setAddressDetails(details);
    } else {
      setAddress("");
      setAddressDetails(null);
    }
  };

  const handleCityAddressChange = (details?: Location) => {
    if (details) {
      setCityaddress(details.formatted_address);
      setCityAddressDetails(details);
    } else {
      setCityaddress("");
      setCityAddressDetails(null);
    }
  };

  // Populate all form fields when profile data is loaded
  useEffect(() => {
    if (memberProfile) {
      console.log("Populating form fields from memberProfile:", memberProfile);
      setSelectedPrefix(memberProfile.prefix ?? "");
      setFirstName(memberProfile.first_name ?? "");
      setMiddleName(memberProfile.middle_name ?? "");
      setLastName(memberProfile.last_name ?? "");
      setSelectedSuffix(memberProfile.member_suffix ?? "");
      setSelectedDepartment(memberProfile.department ?? "");
      setSelectedPosition(memberProfile.position ?? "");
      setProfileimg(memberProfile.profileimg ?? null);
      setCertificate1(memberProfile.certificate1 ?? "");
      setPhonenumber(memberProfile.phonenumber ?? "");
      setSelectedGender(memberProfile.gender ?? "");
      setDob(memberProfile.dob ? dayjs(memberProfile.dob).format("DD-MM-YYYY") : "");

      // Handle address - preserve object if exists
      if (typeof memberProfile.address === "object" && memberProfile.address) {
        setAddress(
          memberProfile.address.display_name ?? memberProfile.address.name ?? ""
        );
        setAddressDetails(memberProfile.address as Location);
      } else {
        setAddress(
          typeof memberProfile.address === "string" ? memberProfile.address : ""
        );
      }

      // Handle cityaddress - preserve object if exists
      if (
        typeof memberProfile.cityaddress === "object" &&
        memberProfile.cityaddress
      ) {
        setCityaddress(
          memberProfile.cityaddress.display_name ??
          memberProfile.cityaddress.name ??
          ""
        );
        setCityAddressDetails(memberProfile.cityaddress as Location);
      } else {
        setCityaddress(
          typeof memberProfile.cityaddress === "string"
            ? memberProfile.cityaddress
            : ""
        );
      }

      setDescription(memberProfile.description ?? "");
      setWorkemail(memberProfile.workemail ?? "");
      setSkill(Array.isArray(memberProfile.skill) ? memberProfile.skill : []);
      setSkillinterests(
        Array.isArray(memberProfile.skillinterests)
          ? memberProfile.skillinterests
          : []
      );
      setSupervisor(memberProfile.supervisor ?? "");
      setSelectedSeniority(memberProfile.seniority ?? "");
      setResponsibility(memberProfile.responsibility ?? "");
      setEmergencyroles(memberProfile.emergencyroles ?? "");
      setWhatsapp(memberProfile.whatsapp ?? "");
      setTwitter(memberProfile.twitter ?? "");
      setFacebook(memberProfile.facebook ?? "");
      setLinkedin(memberProfile.linkedin ?? "");
      setEmail(memberProfile.email ?? "");
      setSelectedConsultation(memberProfile.consultation ?? "");
      setSelectedContract(memberProfile.contract ?? "");
      setSelectedMarketplace(memberProfile.marketplace ?? "");
      setSelectedOpportunities(memberProfile.opportunities ?? "");
      console.log("Form fields populated successfully");
    } else {
      console.log("memberProfile is null or undefined, fields not populated");
    }
  }, [memberProfile]);

  // Handle save changes
  const handleSaveChanges = async () => {
    if (!memberId) {
      console.error("Member ID is required");
      return;
    }

    // Clear existing errors
    setErrors({});
    const newErrors: Record<string, string> = {};

    // Validation logic
    if (!firstName?.trim()) {
      newErrors.firstName = "this field required";
    }
    if (!lastName?.trim()) {
      newErrors.lastName = "this field required";
    }
    if (!selectedDepartment) {
      newErrors.department = "this field required";
    }
    if (!selectedPosition) {
      newErrors.position = "this field required";
    }
    if (!phonenumber?.trim()) {
      newErrors.phonenumber = "this field required";
    } else if (!/^\d{10}$/.test(phonenumber.trim())) {
      newErrors.phonenumber = "please enter 10 digit number";
    }
    if (!selectedGender?.trim()) {
      newErrors.gender = "this field required";
    }
    if (!dob) {
      newErrors.dob = "this field required";
    }
    if (!address?.trim()) {
      newErrors.address = "this field required";
    }
    if (!cityaddress?.trim()) {
      newErrors.cityaddress = "this field required";
    }
    if (!skill || skill.length === 0) {
      newErrors.skill = "this field required";
    }
    if (!skillinterests || skillinterests.length === 0) {
      newErrors.skillinterests = "this field required";
    }
    if (!selectedSeniority) {
      newErrors.seniority = "this field required";
    }
    if (!email?.trim()) {
      newErrors.email = "this field required";
    }
    if (!selectedConsultation) {
      newErrors.consultation = "this field required";
    }
    if (!selectedContract) {
      newErrors.contract = "this field required";
    }
    if (!selectedMarketplace) {
      newErrors.marketplace = "this field required";
    }
    if (!selectedOpportunities) {
      newErrors.opportunities = "this field required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Scroll to the first error if possible, or just stop
      const firstErrorField = Object.keys(newErrors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    // Format date if exists - handle DD-MM-YYYY format
    let dobFormatted = null;
    if (dob) {
      if (dob.includes("-")) {
        const parts = dob.split("-");
        // If it looks like DD-MM-YYYY
        if (parts.length === 3 && parts[2].length === 4) {
          dobFormatted = `${parts[2]}-${parts[1]}-${parts[0]}`;
        } else {
          dobFormatted = dayjs(dob).format("YYYY-MM-DD");
        }
      } else {
        dobFormatted = dayjs(dob).format("YYYY-MM-DD");
      }
    }
    const userId = localStorage.getItem("userId");

    // Prefer fresh structured details (Google uses place_id, not id). Avoid keeping stale memberProfile.* when user picked a new place.
    const addressValue =
      addressDetails && hasStructuredAddressPayload(addressDetails)
        ? addressDetails
        : typeof memberProfile?.address === "object" &&
          memberProfile?.address &&
          (memberProfile.address as { id?: string }).id
          ? memberProfile.address
          : address.trim() || null;

    const cityaddressValue =
      cityAddressDetails && hasStructuredAddressPayload(cityAddressDetails)
        ? cityAddressDetails
        : typeof memberProfile?.cityaddress === "object" &&
          memberProfile?.cityaddress &&
          (memberProfile.cityaddress as { id?: string }).id
          ? memberProfile.cityaddress
          : cityaddress.trim() || null;

    // Build complete payload with all fields (matching MemberRegistrationPage structure)
    // Include all fields with null for missing ones (as per user requirement)
    const tenantId = localStorage.getItem("tenant");
    const rawData: Record<string, any> = {
      verification: "Pending",
      member_status: "true",
      tenant: tenantId ?? null,
      jdid: null,
      first_name: firstName.trim() || null,
      middle_name: middleName.trim() || null,
      last_name: lastName.trim() || null,
      member_suffix: selectedSuffix || null,
      profileimg: profileimg ?? null,
      phonenumber: phonenumber.trim() || null,
      gender: selectedGender ?? null,
      dob: dobFormatted ?? null,
      address: addressValue ?? null,
      profession: null,
      employer: null,
      member_industry: null,
      organization: null,
      governmentid: null,
      governmentidnumber: null,
      govimage: null,
      description: description.trim() || null,
      username: userId ?? null,
      certificate1: certificate1.trim() || null,
      certificate2: null,
      prefix: selectedPrefix || null,
      department: selectedDepartment || null,
      position: selectedPosition || null,
      profilephoto: null,
      organizationid: OrganizationId ?? null,
      cityaddress: cityaddressValue ?? null,
      workemail: workemail.trim() || null,
      workphone: null,
      supervisor: supervisor.trim() || null,
      seniority: selectedSeniority || null,
      responsibility: responsibility.trim() || null,
      emergencyroles: emergencyroles.trim() || null,
      skillinterests: skillinterests.length > 0 ? skillinterests : null,
      whatsapp: whatsapp.trim() || null,
      twitter: twitter.trim() || null,
      facebook: facebook.trim() || null,
      linkedin: linkedin.trim() || null,
      consultation: selectedConsultation || null,
      contract: selectedContract || null,
      marketplace: selectedMarketplace || null,
      rdate: null,
      opportunities: selectedOpportunities || null,
      awards: null,
      certificate: null,
      approve: null,
      email: email.trim() || null,
      retire: null,
      skill: skill.length > 0 ? skill : null,
    };

    console.log("Member ID being updated:", memberId);
    console.log("Final payload structure:", rawData);

    // For PUT request, pass complete payload with all fields (null for missing ones)
    // This matches the MemberRegistrationPage update logic
    updateProfileMutation.mutate(rawData);
  };

  // Handle logout - clear localStorage and navigate to auth page
  const handleLogout = () => {
    // Clear all specified localStorage items
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("zulipToken");
    localStorage.removeItem("zulipUserId");
    localStorage.removeItem("userId");
    localStorage.removeItem("userInfo");
    localStorage.removeItem("memberId");

    // Navigate to auth page
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="mb-4 flex items-center gap-4">
            <BackButton fallback={CitycollabNavigationRoutes.NEW_DESIGN} />
            <div>
              <h1 className="text-3xl font-bold text-foreground">Settings</h1>
            </div>
          </div>
          <p className="ml-14 text-muted-foreground">
            Manage your account preferences and settings
          </p>
        </div>

        <div className="space-y-6">
          {/* Profile Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Profile Settings</CardTitle>
              <CardDescription>
                Update your personal information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoadingProfile ? (
                <div className="py-4 text-center text-sm text-muted-foreground">
                  Loading profile data...
                </div>
              ) : !memberId ? (
                <div className="py-4 text-center text-sm text-destructive">
                  Member ID is required. Please log in again.
                </div>
              ) : !memberProfile ? (
                <div className="py-4 text-center text-sm text-destructive">
                  <p>Profile not found (404).</p>
                  <p className="mt-2 text-xs">Member ID: {memberId}</p>
                  <p className="mt-1 text-xs">
                    The member profile may not exist or the ID is incorrect.
                  </p>
                </div>
              ) : (
                <>
                  {/* Personal Information Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">
                      Personal Information
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
                      <div className="space-y-2">
                        <Label htmlFor="prefix">Prefix</Label>
                        <div
                          id="prefix"
                          className="relative"
                          onClick={e => e.stopPropagation()}
                        >
                          <div
                            onClick={e => {
                              e.stopPropagation();
                              setShowPrefixOptions(!showPrefixOptions);
                            }}
                            className="flex h-10 w-full cursor-pointer items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                            style={{
                              color: selectedPrefix
                                ? undefined
                                : "hsl(var(--muted-foreground))",
                            }}
                          >
                            <span>
                              {prefixOptions?.find(
                                (opt: any) => opt.value === selectedPrefix
                              )?.label ?? "Prefix"}
                            </span>
                            <ChevronDown className="h-4 w-4 opacity-50" />
                          </div>
                          {showPrefixOptions && (
                            <div
                              className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
                              onClick={e => e.stopPropagation()}
                            >
                              <div className="p-1">
                                {prefixOptions?.map((option: any) => (
                                  <div
                                    key={option.value}
                                    className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                    onClick={() => {
                                      setSelectedPrefix(option.value);
                                      setShowPrefixOptions(false);
                                    }}
                                  >
                                    {selectedPrefix === option.value && (
                                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                        ✓
                                      </span>
                                    )}
                                    {option.label}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="firstName">
                          First Name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="firstName"
                          placeholder="First name"
                          className={
                            errors.firstName
                              ? "border-destructive focus-visible:ring-destructive"
                              : ""
                          }
                          value={firstName}
                          onChange={e => setFirstName(e.target.value)}
                        />
                        {errors.firstName && (
                          <p className="mt-1 text-xs text-destructive">
                            {errors.firstName}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2 md:col-span-1">
                        <Label htmlFor="middleName">Middle Name</Label>
                        <Input
                          id="middleName"
                          placeholder="Middle name"
                          value={middleName}
                          onChange={e => setMiddleName(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="lastName">
                          Last Name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="lastName"
                          placeholder="Last name"
                          className={
                            errors.lastName
                              ? "border-destructive focus-visible:ring-destructive"
                              : ""
                          }
                          value={lastName}
                          onChange={e => setLastName(e.target.value)}
                        />
                        {errors.lastName && (
                          <p className="mt-1 text-xs text-destructive">
                            {errors.lastName}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div className="space-y-2">
                        <Label htmlFor="memberSuffix">Suffix</Label>
                        <div
                          id="suffix"
                          className="relative"
                          onClick={e => e.stopPropagation()}
                        >
                          <div
                            onClick={e => {
                              e.stopPropagation();
                              setShowSuffixOptions(!showSuffixOptions);
                            }}
                            className="flex h-10 w-full cursor-pointer items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                            style={{
                              color: selectedSuffix
                                ? undefined
                                : "hsl(var(--muted-foreground))",
                            }}
                          >
                            <span>
                              {suffixOptions?.find(
                                (opt: any) => opt.value === selectedSuffix
                              )?.label ?? "Suffix"}
                            </span>
                            <ChevronDown className="h-4 w-4 opacity-50" />
                          </div>
                          {showSuffixOptions && (
                            <div
                              className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
                              onClick={e => e.stopPropagation()}
                            >
                              <div className="p-1">
                                {suffixOptions?.map((option: any) => (
                                  <div
                                    key={option.value}
                                    className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                    onClick={() => {
                                      setSelectedSuffix(option.value);
                                      setShowSuffixOptions(false);
                                    }}
                                  >
                                    {selectedSuffix === option.value && (
                                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                        ✓
                                      </span>
                                    )}
                                    {option.label}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="gender">
                          Gender <span className="text-destructive">*</span>
                        </Label>
                        <div
                          id="gender"
                          className="relative"
                          onClick={e => e.stopPropagation()}
                        >
                          <div
                            onClick={e => {
                              e.stopPropagation();
                              setShowGenderOptions(!showGenderOptions);
                            }}
                            className={`flex h-10 w-full cursor-pointer items-center justify-between rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${errors.gender ? "border-destructive focus:ring-destructive" : "border-input"}`}
                            style={{
                              color:
                                selectedGender && selectedGender.trim() !== ""
                                  ? undefined
                                  : "hsl(var(--muted-foreground))",
                            }}
                          >
                            <span>
                              {selectedGender && selectedGender.trim() !== ""
                                ? selectedGender
                                : "Select Gender"}
                            </span>
                            <ChevronDown className="h-4 w-4 opacity-50" />
                          </div>
                          {errors.gender && (
                            <p className="mt-1 text-xs text-destructive">
                              {errors.gender}
                            </p>
                          )}
                          {showGenderOptions && (
                            <div
                              className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
                              onClick={e => e.stopPropagation()}
                            >
                              <div className="p-1">
                                {genderOptions.map(option => (
                                  <div
                                    key={option}
                                    className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                    onClick={() => {
                                      setSelectedGender(option);
                                      setShowGenderOptions(false);
                                    }}
                                  >
                                    {selectedGender === option && (
                                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                        ✓
                                      </span>
                                    )}
                                    {option}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="dob">
                          Date of Birth{" "}
                          <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="dob"
                          type="text"
                          placeholder="DD-MM-YYYY"
                          className={
                            errors.dob
                              ? "border-destructive focus-visible:ring-destructive"
                              : ""
                          }
                          value={dob}
                          onChange={e => setDob(e.target.value)}
                        />
                        {errors.dob && (
                          <p className="mt-1 text-xs text-destructive">
                            {errors.dob}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <Separator className="my-6" />

                  {/* Professional Information Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">
                      Professional Information
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="department">
                          Department <span className="text-destructive">*</span>
                        </Label>
                        <div
                          id="department"
                          className="relative"
                          onClick={e => e.stopPropagation()}
                        >
                          <div
                            onClick={e => {
                              if (isDepartmentReadonly) {
                                return;
                              }
                              e.stopPropagation();
                              setShowDepartmentOptions(!showDepartmentOptions);
                            }}
                            className={`flex h-10 w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${isDepartmentReadonly ? "cursor-not-allowed opacity-60" : "cursor-pointer"} ${errors.department ? "border-destructive focus:ring-destructive" : "border-input"}`}
                            style={{
                              color: selectedDepartment
                                ? undefined
                                : "hsl(var(--muted-foreground))",
                              backgroundColor: isDepartmentReadonly
                                ? "hsl(var(--muted))"
                                : undefined,
                            }}
                          >
                            <span>
                              {departmentOptions?.find(
                                (opt: any) => opt.id === selectedDepartment
                              )?.department ?? "Select Department"}
                            </span>
                            {!isDepartmentReadonly && (
                              <ChevronDown className="h-4 w-4 opacity-50" />
                            )}
                          </div>
                          {errors.department && (
                            <p className="mt-1 text-xs text-destructive">
                              {errors.department}
                            </p>
                          )}
                          {showDepartmentOptions && !isDepartmentReadonly && (
                            <div
                              className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
                              onClick={e => e.stopPropagation()}
                            >
                              <div className="p-1">
                                {departmentOptions?.map((option: any) => (
                                  <div
                                    key={option.id}
                                    className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                    onClick={() => {
                                      setSelectedDepartment(option.id);
                                      setShowDepartmentOptions(false);
                                    }}
                                  >
                                    {selectedDepartment === option.id && (
                                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                        ✓
                                      </span>
                                    )}
                                    {option.department}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="position">
                          Position <span className="text-destructive">*</span>
                        </Label>
                        <div
                          id="position"
                          className="relative"
                          onClick={e => e.stopPropagation()}
                        >
                          <div
                            onClick={e => {
                              if (isPositionReadonly) {
                                return;
                              }
                              e.stopPropagation();
                              setShowPositionOptions(!showPositionOptions);
                            }}
                            className={`flex h-10 w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${isPositionReadonly ? "cursor-not-allowed opacity-60" : "cursor-pointer"} ${errors.position ? "border-destructive focus:ring-destructive" : "border-input"}`}
                            style={{
                              color: selectedPosition
                                ? undefined
                                : "hsl(var(--muted-foreground))",
                              backgroundColor: isPositionReadonly
                                ? "hsl(var(--muted))"
                                : undefined,
                            }}
                          >
                            <span>
                              {positionOptions?.find(
                                (opt: any) => opt.id === selectedPosition
                              )?.position ?? "Select Position"}
                            </span>
                            {!isPositionReadonly && (
                              <ChevronDown className="h-4 w-4 opacity-50" />
                            )}
                          </div>
                          {errors.position && (
                            <p className="mt-1 text-xs text-destructive">
                              {errors.position}
                            </p>
                          )}
                          {showPositionOptions && !isPositionReadonly && (
                            <div
                              className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
                              onClick={e => e.stopPropagation()}
                            >
                              <div className="p-1">
                                {positionOptions?.map((option: any) => (
                                  <div
                                    key={option.id}
                                    className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                    onClick={() => {
                                      setSelectedPosition(option.id);
                                      setShowPositionOptions(false);
                                    }}
                                  >
                                    {selectedPosition === option.id && (
                                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                        ✓
                                      </span>
                                    )}
                                    {option.position}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="seniority">
                          Seniority <span className="text-destructive">*</span>
                        </Label>
                        <div
                          id="seniority"
                          className="relative"
                          onClick={e => e.stopPropagation()}
                        >
                          <div
                            onClick={e => {
                              e.stopPropagation();
                              setShowSeniorityOptions(!showSeniorityOptions);
                            }}
                            className={`flex h-10 w-full cursor-pointer items-center justify-between rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${errors.seniority ? "border-destructive focus:ring-destructive" : "border-input"}`}
                            style={{
                              color: selectedSeniority
                                ? undefined
                                : "hsl(var(--muted-foreground))",
                            }}
                          >
                            <span>
                              {seniorityOptions.find(
                                opt => opt.value === selectedSeniority
                              )?.label ??
                                (selectedSeniority ||
                                  "Select Seniority Level")}
                            </span>
                            <ChevronDown className="h-4 w-4 opacity-50" />
                          </div>
                          {errors.seniority && (
                            <p className="mt-1 text-xs text-destructive">
                              {errors.seniority}
                            </p>
                          )}
                          {showSeniorityOptions && (
                            <div
                              className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
                              onClick={e => e.stopPropagation()}
                            >
                              <div className="p-1">
                                {seniorityOptions.map(option => (
                                  <div
                                    key={option.value}
                                    className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                    onClick={() => {
                                      setSelectedSeniority(option.value);
                                      setShowSeniorityOptions(false);
                                    }}
                                  >
                                    {selectedSeniority === option.value && (
                                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                        ✓
                                      </span>
                                    )}
                                    {option.label}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="certificate1">Medal</Label>
                        <Input
                          id="certificate1"
                          placeholder="Medal"
                          value={certificate1}
                          onChange={e => setCertificate1(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator className="my-6" />

                  {/* Roles & Responsibilities Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">
                      Roles & Responsibilities
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="supervisor">Supervisor</Label>
                        <Input
                          id="supervisor"
                          placeholder="Supervisor"
                          value={supervisor}
                          onChange={e => setSupervisor(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="responsibility">
                          Area of Responsibility
                        </Label>
                        <Input
                          id="responsibility"
                          placeholder="Area of responsibility"
                          value={responsibility}
                          onChange={e => setResponsibility(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="emergencyroles">Emergency Roles</Label>
                        <Input
                          id="emergencyroles"
                          placeholder="Emergency roles"
                          value={emergencyroles}
                          onChange={e => setEmergencyroles(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Input
                          id="description"
                          placeholder="Description"
                          value={description}
                          onChange={e => setDescription(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator className="my-6" />

                  {/* Skills & Interests Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">Skills & Interests</h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="skill">
                          Government Skill{" "}
                          <span className="text-destructive">*</span>
                        </Label>
                        <div
                          id="skill"
                          className="relative"
                          onClick={e => e.stopPropagation()}
                        >
                          <div
                            className={`flex min-h-[40px] w-full cursor-pointer flex-wrap items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${errors.skill ? "border-destructive focus:ring-destructive" : "border-input"}`}
                            onClick={e => {
                              e.stopPropagation();
                              setShowSkillsOptions(!showSkillsOptions);
                            }}
                          >
                            {skill.length > 0 ? (
                              skill.map(skillValue => {
                                const option = skillsOptions?.find(
                                  (opt: any) => opt.value === skillValue
                                );
                                return (
                                  <span
                                    key={`skill-${skillValue}`}
                                    className="flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm text-primary"
                                  >
                                    {option?.label ?? skillValue}
                                    <button
                                      type="button"
                                      onClick={e => {
                                        e.stopPropagation();
                                        setSkill(
                                          skill.filter(
                                            item => item !== skillValue
                                          )
                                        );
                                      }}
                                      className="text-primary hover:text-primary/80"
                                    >
                                      ×
                                    </button>
                                  </span>
                                );
                              })
                            ) : (
                              <span className="text-sm text-muted-foreground">
                                Select Government Skill
                              </span>
                            )}
                            <ChevronDown className="ml-auto h-4 w-4 opacity-50" />
                          </div>
                          {errors.skill && (
                            <p className="mt-1 text-xs text-destructive">
                              {errors.skill}
                            </p>
                          )}
                          {showSkillsOptions && (
                            <div
                              className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
                              onClick={e => e.stopPropagation()}
                            >
                              <div className="p-1">
                                {skillsOptions?.map((option: any) => (
                                  <div
                                    key={option.value}
                                    className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                    onClick={() => {
                                      setSkill(
                                        skill.includes(option.value)
                                          ? skill.filter(
                                            s => s !== option.value
                                          )
                                          : [...skill, option.value]
                                      );
                                    }}
                                  >
                                    {skill.includes(option.value) && (
                                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                        ✓
                                      </span>
                                    )}
                                    <span>{option.label}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="skillinterests">
                          Skill Interests{" "}
                          <span className="text-destructive">*</span>
                        </Label>
                        <div
                          id="skillinterests"
                          className="relative"
                          onClick={e => e.stopPropagation()}
                        >
                          <div
                            className={`flex min-h-[40px] w-full cursor-pointer flex-wrap items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${errors.skillinterests ? "border-destructive focus:ring-destructive" : "border-input"}`}
                            onClick={e => {
                              e.stopPropagation();
                              setShowSkillInterestsOptions(
                                !showSkillInterestsOptions
                              );
                            }}
                          >
                            {skillinterests.length > 0 ? (
                              skillinterests.map(skillInterestValue => {
                                const option = skillInterestsOptions.find(
                                  opt => opt.value === skillInterestValue
                                );
                                return (
                                  <span
                                    key={`skillinterest-${skillInterestValue}`}
                                    className="flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm text-primary"
                                  >
                                    {option?.label ?? skillInterestValue}
                                    <button
                                      type="button"
                                      onClick={e => {
                                        e.stopPropagation();
                                        setSkillinterests(
                                          skillinterests.filter(
                                            item => item !== skillInterestValue
                                          )
                                        );
                                      }}
                                      className="text-primary hover:text-primary/80"
                                    >
                                      ×
                                    </button>
                                  </span>
                                );
                              })
                            ) : (
                              <span className="text-sm text-muted-foreground">
                                Select Skill Interests
                              </span>
                            )}
                            <ChevronDown className="ml-auto h-4 w-4 opacity-50" />
                          </div>
                          {errors.skillinterests && (
                            <p className="mt-1 text-xs text-destructive">
                              {errors.skillinterests}
                            </p>
                          )}
                          {showSkillInterestsOptions && (
                            <div
                              className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
                              onClick={e => e.stopPropagation()}
                            >
                              <div className="p-1">
                                {skillInterestsOptions.map(option => (
                                  <div
                                    key={option.value}
                                    className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                    onClick={() => {
                                      setSkillinterests(
                                        skillinterests.includes(option.value)
                                          ? skillinterests.filter(
                                            s => s !== option.value
                                          )
                                          : [...skillinterests, option.value]
                                      );
                                    }}
                                  >
                                    {skillinterests.includes(option.value) && (
                                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                        ✓
                                      </span>
                                    )}
                                    <span>{option.label}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information & Social Media Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">
                      Contact Information & Social Media
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                      <div className="space-y-2">
                        <Label htmlFor="email">
                          Email <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="email"
                          placeholder="Email"
                          className={
                            errors.email
                              ? "border-destructive focus-visible:ring-destructive"
                              : ""
                          }
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                        />
                        {errors.email && (
                          <p className="mt-1 text-xs text-destructive">
                            {errors.email}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="workemail">Work Email</Label>
                        <Input
                          id="workemail"
                          type="email"
                          placeholder="Work email"
                          value={workemail}
                          onChange={e => setWorkemail(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phonenumber">
                          Phone Number{" "}
                          <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="phonenumber"
                          placeholder="Phone number"
                          className={
                            errors.phonenumber
                              ? "border-destructive focus-visible:ring-destructive"
                              : ""
                          }
                          value={phonenumber}
                          onChange={e => setPhonenumber(e.target.value)}
                        />
                        {errors.phonenumber && (
                          <p className="mt-1 text-xs text-destructive">
                            {errors.phonenumber}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="whatsapp">WhatsApp</Label>
                        <Input
                          id="whatsapp"
                          placeholder="WhatsApp"
                          value={whatsapp}
                          onChange={e => setWhatsapp(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="twitter">Twitter</Label>
                        <Input
                          id="twitter"
                          placeholder="Twitter"
                          value={twitter}
                          onChange={e => setTwitter(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="facebook">Facebook</Label>
                        <Input
                          id="facebook"
                          placeholder="Facebook"
                          value={facebook}
                          onChange={e => setFacebook(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="linkedin">LinkedIn</Label>
                        <Input
                          id="linkedin"
                          placeholder="LinkedIn"
                          value={linkedin}
                          onChange={e => setLinkedin(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator className="my-6" />

                  {/* Address Information Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">Address Information</h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="address">
                          Department Address{" "}
                          <span className="text-destructive">*</span>
                        </Label>
                        <div
                          id="address"
                          className="[&_input]:flex [&_input]:h-10 [&_input]:w-full [&_input]:rounded-md [&_input]:border [&_input]:border-input [&_input]:bg-background [&_input]:px-3 [&_input]:py-2 [&_input]:text-sm [&_input]:ring-offset-background [&_input]:placeholder:text-muted-foreground [&_input]:focus-visible:outline-none [&_input]:focus-visible:ring-2 [&_input]:focus-visible:ring-ring [&_input]:focus-visible:ring-offset-2 [&_input]:disabled:cursor-not-allowed [&_input]:disabled:opacity-50"
                        >
                          <GoogleAddressInput
                            value={address}
                            placeholder="Enter your department address"
                            className={`w-full ${errors.address ? "border-destructive focus-visible:ring-destructive" : ""}`}
                            onChange={handleAddressChange}
                            countryRestriction=""
                            types={["establishment", "geocode"]}
                          />
                        </div>
                        {errors.address && (
                          <p className="mt-1 text-xs text-destructive">
                            {errors.address}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cityaddress">
                          City Address{" "}
                          <span className="text-destructive">*</span>
                        </Label>
                        <div
                          id="cityaddress"
                          className="[&_input]:flex [&_input]:h-10 [&_input]:w-full [&_input]:rounded-md [&_input]:border [&_input]:border-input [&_input]:bg-background [&_input]:px-3 [&_input]:py-2 [&_input]:text-sm [&_input]:ring-offset-background [&_input]:placeholder:text-muted-foreground [&_input]:focus-visible:outline-none [&_input]:focus-visible:ring-2 [&_input]:focus-visible:ring-ring [&_input]:focus-visible:ring-offset-2 [&_input]:disabled:cursor-not-allowed [&_input]:disabled:opacity-50"
                        >
                          <GoogleAddressInput
                            value={cityaddress}
                            placeholder="Enter your city address"
                            className={`w-full ${errors.cityaddress ? "border-destructive focus-visible:ring-destructive" : ""}`}
                            onChange={handleCityAddressChange}
                            countryRestriction=""
                            types={["establishment", "geocode"]}
                          />
                        </div>
                        {errors.cityaddress && (
                          <p className="mt-1 text-xs text-destructive">
                            {errors.cityaddress}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <Separator className="my-6" />

                  {/* Additional Preferences Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">
                      Additional Preferences
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                      {/* Open to Peer Consultation */}
                      <div className="space-y-2">
                        <Label>
                          Peer Consultation{" "}
                          <span className="text-destructive">*</span>
                        </Label>
                        <div
                          id="consultation"
                          className="relative"
                          onClick={e => e.stopPropagation()}
                        >
                          <div
                            onClick={e => {
                              e.stopPropagation();
                              setShowConsultationOptions(
                                !showConsultationOptions
                              );
                            }}
                            className={`flex h-10 w-full cursor-pointer items-center justify-between rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${errors.consultation ? "border-destructive focus:ring-destructive" : "border-input"}`}
                            style={{
                              color: selectedConsultation
                                ? undefined
                                : "hsl(var(--muted-foreground))",
                            }}
                          >
                            <span>{selectedConsultation || "Select"}</span>
                            <ChevronDown className="h-4 w-4 opacity-50" />
                          </div>
                          {errors.consultation && (
                            <p className="mt-1 text-xs text-destructive">
                              {errors.consultation}
                            </p>
                          )}
                          {showConsultationOptions && (
                            <div
                              className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
                              onClick={e => e.stopPropagation()}
                            >
                              <div className="p-1">
                                {["Yes", "No"].map(
                                  option => (
                                    <div
                                      key={option}
                                      className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                      onClick={() => {
                                        setSelectedConsultation(option);
                                        setShowConsultationOptions(false);
                                      }}
                                    >
                                      {selectedConsultation === option && (
                                        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                          ✓
                                        </span>
                                      )}
                                      {option}
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Contract Type */}
                      <div className="space-y-2">
                        <Label>
                          Contract Type{" "}
                          <span className="text-destructive">*</span>
                        </Label>
                        <div
                          id="contract"
                          className="relative"
                          onClick={e => e.stopPropagation()}
                        >
                          <div
                            onClick={e => {
                              e.stopPropagation();
                              setShowContractOptions(!showContractOptions);
                            }}
                            className={`flex h-10 w-full cursor-pointer items-center justify-between rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${errors.contract ? "border-destructive focus:ring-destructive" : "border-input"}`}
                            style={{
                              color: selectedContract
                                ? undefined
                                : "hsl(var(--muted-foreground))",
                            }}
                          >
                            <span>{selectedContract || "Select"}</span>
                            <ChevronDown className="h-4 w-4 opacity-50" />
                          </div>
                          {errors.contract && (
                            <p className="mt-1 text-xs text-destructive">
                              {errors.contract}
                            </p>
                          )}
                          {showContractOptions && (
                            <div
                              className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
                              onClick={e => e.stopPropagation()}
                            >
                              <div className="p-1">
                                {["Yes", "No"].map(option => (
                                  <div
                                    key={option}
                                    className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                    onClick={() => {
                                      setSelectedContract(option);
                                      setShowContractOptions(false);
                                    }}
                                  >
                                    {selectedContract === option && (
                                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                        ✓
                                      </span>
                                    )}
                                    {option}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Internal Marketplace */}
                      <div className="space-y-2">
                        <Label>
                          Visible in Marketplace{" "}
                          <span className="text-destructive">*</span>
                        </Label>
                        <div
                          id="marketplace"
                          className="relative"
                          onClick={e => e.stopPropagation()}
                        >
                          <div
                            onClick={e => {
                              e.stopPropagation();
                              setShowMarketplaceOptions(
                                !showMarketplaceOptions
                              );
                            }}
                            className={`flex h-10 w-full cursor-pointer items-center justify-between rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${errors.marketplace ? "border-destructive focus:ring-destructive" : "border-input"}`}
                            style={{
                              color: selectedMarketplace
                                ? undefined
                                : "hsl(var(--muted-foreground))",
                            }}
                          >
                            <span>{selectedMarketplace || "Select"}</span>
                            <ChevronDown className="h-4 w-4 opacity-50" />
                          </div>
                          {errors.marketplace && (
                            <p className="mt-1 text-xs text-destructive">
                              {errors.marketplace}
                            </p>
                          )}
                          {showMarketplaceOptions && (
                            <div
                              className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
                              onClick={e => e.stopPropagation()}
                            >
                              <div className="p-1">
                                {["Yes", "No"].map(option => (
                                  <div
                                    key={option}
                                    className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                    onClick={() => {
                                      setSelectedMarketplace(option);
                                      setShowMarketplaceOptions(false);
                                    }}
                                  >
                                    {selectedMarketplace === option && (
                                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                        ✓
                                      </span>
                                    )}
                                    {option}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Post-Retirement Opportunities */}
                      <div className="space-y-2">
                        <Label>
                          Post-Retirement Opportunities{" "}
                          <span className="text-destructive">*</span>
                        </Label>
                        <div
                          id="opportunities"
                          className="relative"
                          onClick={e => e.stopPropagation()}
                        >
                          <div
                            onClick={e => {
                              e.stopPropagation();
                              setShowOpportunitiesOptions(
                                !showOpportunitiesOptions
                              );
                            }}
                            className={`flex h-10 w-full cursor-pointer items-center justify-between rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${errors.opportunities ? "border-destructive focus:ring-destructive" : "border-input"}`}
                            style={{
                              color: selectedOpportunities
                                ? undefined
                                : "hsl(var(--muted-foreground))",
                            }}
                          >
                            <span>{selectedOpportunities || "Select"}</span>
                            <ChevronDown className="h-4 w-4 opacity-50" />
                          </div>
                          {errors.opportunities && (
                            <p className="mt-1 text-xs text-destructive">
                              {errors.opportunities}
                            </p>
                          )}
                          {showOpportunitiesOptions && (
                            <div
                              className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
                              onClick={e => e.stopPropagation()}
                            >
                              <div className="p-1">
                                {[
                                  "Advisory Role",
                                  "Contract Work",
                                  "Peer Mentoring",
                                ].map(option => (
                                  <div
                                    key={option}
                                    className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                    onClick={() => {
                                      setSelectedOpportunities(option);
                                      setShowOpportunitiesOptions(false);
                                    }}
                                  >
                                    {selectedOpportunities === option && (
                                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                        ✓
                                      </span>
                                    )}
                                    {option}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleSaveChanges}
                    disabled={updateProfileMutation.isPending}
                    className="mt-6"
                  >
                    {updateProfileMutation.isPending
                      ? "Saving..."
                      : "Save Changes"}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default Settings;
